import React from "react";
import DataTable from "react-data-table-component";
import Spinner from "./iconSvgs/spinner";
import { NoDataFoundIcon } from "../assets/Icons";

interface CustomDatatableProps {
  columns: any[];
  data: any[];
  handleSort?: (column: any, sortDirection: string) => void;
  isLoading?: boolean;
  className?: string;
  fixedHeight?: boolean;
  defaultSortField?: string; // Used as defaultSortFieldId in DataTable
  defaultSortAsc?: boolean;
}

const CustomDatatable: React.FC<CustomDatatableProps> = ({
  columns,
  data,
  handleSort,
  isLoading = false,
  className = "",
  fixedHeight = false,
  defaultSortField,
  defaultSortAsc = true,
}) => {
  const processedColumns = columns.map((column) => {
    const modifiedColumn = { ...column };

    // Auto-wrap long headers between words
    if (
      typeof column.name === "string" &&
      column.name.length > 8 &&
      !column.name.includes("<")
    ) {
      const words = column.name.split(" ");
      modifiedColumn.name = (
        <div className="text-left leading-tight break-words whitespace-normal">
          {words.map((word: string, index: number) => (
            <span key={index} className="inline-block mr-1">
              {word}
            </span>
          ))}
        </div>
      );
    }

    modifiedColumn.style = {
      ...column.style,
      textAlign: "left",
    };

    modifiedColumn.headStyle = {
      ...column.headStyle,
      textAlign: "left",
      whiteSpace: "normal", // ✅ Allow wrapping between words
      wordBreak: "normal", // ✅ Prevent breaking inside a word
    };

    return modifiedColumn;
  });

  const containerStyle = fixedHeight
    ? {
        height: "100%",
        display: "flex",
        flexDirection: "column" as const,
      }
    : {};

  const tableStyle = fixedHeight
    ? {
        flex: 1,
        minHeight: 0,
      }
    : {};

  return (
    <div
      className={`w-full overflow-auto rounded-[10px] shadow-sm border border-gray-200 ${
        fixedHeight ? "h-full flex flex-col" : ""
      }`}
      style={containerStyle}
    >
      <div style={tableStyle} className={fixedHeight ? "flex-1 min-h-0" : ""}>
        <DataTable
          columns={processedColumns}
          data={data}
          highlightOnHover
          sortServer
          striped
          onSort={handleSort}
          defaultSortFieldId={defaultSortField}
          defaultSortAsc={defaultSortAsc}
          fixedHeader={fixedHeight}
          fixedHeaderScrollHeight={fixedHeight ? "100%" : undefined}
          noTableHead={false}
          progressPending={isLoading}
          progressComponent={
            <div className="h-[200px] relative bg-white bg-opacity-75">
              <div className="absolute inset-0 flex items-center justify-center">
                <Spinner size="40" />
              </div>
            </div>
          }
          noDataComponent={
            <div className="h-[300px] flex flex-col items-center justify-center gap-2">
              <NoDataFoundIcon className="w-8 h-8 text-gray-400" />
              <span className="text-gray-600">No data available</span>
            </div>
          }
          customStyles={{
            table: {
              style: {
                width: "100%",
                tableLayout: "fixed",
                borderRadius: "0.625rem",
                backgroundColor: "#fff",
                ...(fixedHeight && { height: "100%" }),
              },
            },
            tableWrapper: {
              style: {
                ...(fixedHeight && {
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }),
              },
            },
            headRow: {
              style: {
                textAlign: "left",
              },
            },
            headCells: {
              style: {
                backgroundColor: "#000000",
                color: "#ffffff",
                fontFamily: "Poppins, sans-serif",
                fontWeight: "normal",
                fontSize: "16px",
                padding: "12px 16px",
                minHeight: "48px",
                height: "auto",
                whiteSpace: "normal", // ✅ Allow line wrapping
                wordBreak: "normal", // ✅ Prevent mid-word breaks
                textAlign: "left",
                lineHeight: "1.3",
              },
            },
            rows: {
              style: {
                textAlign: "left",
              },
            },
            cells: {
              style: {
                padding: "12px 16px",
                whiteSpace: "normal",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minHeight: "40px",
                // maxHeight: '60px',
                textAlign: "left",
              },
            },
          }}
        />
      </div>
    </div>
  );
};

export default CustomDatatable;
